/** Признаки сетевого отказа — ОДИН список на всё приложение.
 *
 *  Их было три, и они расходились. Мутационный проход по слою представления
 *  показал, что в каждом из трёх мест проверялась одна альтернатива из
 *  нескольких, а сравнение списков вскрыло дефект крупнее: `sync.ts` знал
 *  про `enotfound` и `econn`, `AuthGate` — про `timeout` и `aborted`,
 *  а строка состояния обмена не знала ни про то, ни про другое.
 *
 *  Цена расхождения не теоретическая. Один и тот же сбой давал родителю
 *  РАЗНЫЕ слова в зависимости от того, какой слой его поймал: заслон входа
 *  говорил «нет связи» и открывал дверь «заниматься без интернета», а
 *  строка состояния в это же время писала «сервер не отвечает». Про
 *  таймаут — самый частый вид отказа в метро и лифте — расходились ровно
 *  так.
 *
 *  Список текстовый, потому что источники разные: `fetch` в браузере даёт
 *  «Failed to fetch» без кода, Node и обёртки дают код без внятного текста,
 *  а Safari — своё «Load failed». Сравнивать приходится по подстроке.
 */
export const NETWORK_MARKERS = [
  'failed to fetch',
  'failed to send',
  'networkerror',
  'load failed',
  'timeout',
  'etimedout',
  'aborted',
  /** Коды перечислены ЦЕЛИКОМ, а не общим началом `econn`.
   *
   *  Внешнее ревью нашло, что `econn` совпадает внутри обычного английского
   *  слова: «Please reconnect your account before signing in» — reCONNect.
   *  Родителю, которому сервер велел заново привязать запись, показали бы
   *  «проверьте интернет», и он проверял бы роутер. Измерено. */
  'econnrefused',
  'econnreset',
  'econnaborted',
  'enotfound',
] as const;

/** Похоже ли на сетевой отказ по тексту и коду.
 *
 *  Код приклеивается к сообщению намеренно: у части отказов внятен только
 *  он (`ECONNREFUSED`), у части — только текст. Имя ошибки тоже: браузер
 *  шлёт «signal timed out» с именем `TimeoutError`, и без имени слова
 *  `timeout` в строке не окажется вовсе.
 */
export function looksOffline(error: unknown): boolean {
  const e = error as { message?: string; code?: string; name?: string } | null;
  /** Ответ СЕРВЕРА — не сеть, даже если в его тексте есть «timeout» или
   *  «aborted». Внешнее ревью перед выпуском 2026-09-15: отказ Postgres
   *  «canceling statement due to statement timeout» (57014) и «current
   *  transaction is aborted» (25P02) уходили в «нет связи», а строка
   *  состояния обмена при «нет связи» НЕ зовёт `report` — серверная беда
   *  становилась для родителя его метро и не доходила до Sentry. До общего
   *  списка строка состояния таймаут сетью не считала, то есть это был
   *  регресс сведения списков.
   *
   *  Код базы (SQLSTATE) — пять знаков, среди них цифра: 57014, 42501,
   *  P0001, U0001. У сетевого отказа supabase-js код пустой, у Node — длинное
   *  имя (ECONNREFUSED); требование цифры отсекает пятибуквенный EPIPE. */
  if (typeof e?.code === 'string' && /^(?=[0-9A-Z]*\d)[0-9A-Z]{5}$/.test(e.code)) return false;
  const text = `${e?.message ?? String(error)} ${e?.code ?? ''} ${e?.name ?? ''}`.toLowerCase();
  return NETWORK_MARKERS.some((marker) => text.includes(marker));
}
